/* eslint-disable react-hooks/exhaustive-deps */
import { Close, FactCheck, Search, SpeakerNotes } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { FormTextField, GridCardContent } from "components/atoms";
import { FormikProvider, useFormik } from "formik";
import { AlertHelper } from "helpers";
import { EAlertType } from "helpers/alertHelper";
import { usePaginationRequest } from "hooks";
import { chain, filter, isEmpty, orderBy, sum, take, toNumber } from "lodash";
import {
  IApiResponse,
  IGetAllLocationsReportRequest,
  IGetZoneLineRequest,
  ILocation,
  ILocationReport,
  IPaginationRequest,
  IWarehouse,
  IZone,
  IZoneLine,
} from "model";
import { IGetAllLocationByZoneRequest } from "model/ILocation";
import BasePage from "pages/base";
import React, { useEffect, useMemo, useState } from "react";
import {
  MasterDataApiService,
  ReportApiService,
  ZoneApiService,
} from "services";
import { FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import LegendModal from "./components/LegendModal";

interface IWarehouseLayoutContext {
  getWarehouseLoading?: boolean;
  getZoneLoading?: boolean;
  setGetWarehouseLoading?: (loading: boolean) => void;
  setGetZoneLoading?: (loading: boolean) => void;
  searchResult?: ILocationReport[];
}

const WarehouseLayoutContext = React.createContext<IWarehouseLayoutContext>({});
const useWarehouseLayoutContext = () =>
  React.useContext(WarehouseLayoutContext);

const WarehouseLayout = () => {
  const [getWarehouseLoading, setGetWarehouseLoading] = useState(false);
  const [getZoneLoading, setGetZoneLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const [currentWarehouse, setCurrentWarehouse] = useState("");
  const [currentZone, setCurrentZone] = useState("");

  const [searchResult, setSearchResult] = useState<ILocationReport[]>([]);

  const formik = useFormik({
    initialValues: {
      product: "",
      productLot: "",
    },
    onSubmit: () => {
      const request: IGetAllLocationsReportRequest = {
        zone: "",
        warehouse: "",
        line: "",
        product: formik.values.product,
        productLot: formik.values.productLot,
        type: 0,
        user: "",
        pageNumber: 1,
        pageSize: 99999,
      };
      getLocationDetail(request);
    },
  });

  const getLocationDetail = async (request: IGetAllLocationsReportRequest) => {
    setLoading(true);
    const result = (await ReportApiService.getAllLocationsReport(
      request
    )) as IApiResponse<ILocationReport[]>;
    if (result.succeeded) {
      setSearchResult(result.data!);
    } else {
      AlertHelper.showAlert("Create Successfully", EAlertType.SUCCESS);
    }
    setLoading(false);
  };

  const groupLocationListByZone = (data?: ILocationReport[]) => {
    return chain(data)
      .groupBy("zoneCode")
      .map((value, key) => {
        return {
          zoneCode: key,
          zoneName: value[0].zoneName,
          values: chain(value)
            .groupBy("lineCode")
            .map((v, k) => {
              return {
                lineCode: k,
                lineName: v[0].lineName,
                values: v,
              };
            })
            .value(),
        };
      })
      .value();
  };

  return (
    <WarehouseLayoutContext.Provider
      value={{
        getWarehouseLoading,
        getZoneLoading,
        setGetWarehouseLoading,
        setGetZoneLoading,
        searchResult,
      }}
    >
      <BasePage loading={false}>
        <Box style={{ display: "flex", flex: 1, flexDirection: "row" }}>
          <GridCardContent sx={{ flex: 1 }}>
            <Box sx={{ p: 2, width: "100%" }}>
              <Box
                style={{
                  justifyContent: "space-between",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Typography variant="h3">
                  {I18nTranslator(translations.report.warehouseLayout)}
                </Typography>
                <Stack direction={"row"} spacing={1}>
                  <LoadingButton
                    loading={loading || getWarehouseLoading || getZoneLoading}
                    variant="contained"
                    disabled={
                      isEmpty(currentWarehouse) ||
                      isEmpty(currentZone) ||
                      Object.values(formik.values).every((e) => isEmpty(e))
                    }
                    onClick={formik.submitForm}
                    startIcon={<Search />}
                  >
                    {I18nTranslator(translations.button.search)}
                  </LoadingButton>
                  {!isEmpty(searchResult) && (
                    <Badge badgeContent={searchResult.length} color="primary">
                      <Button
                        variant="contained"
                        disabled={isEmpty(searchResult)}
                        onClick={() => setVisible(true)}
                        startIcon={<FactCheck />}
                      >
                        {I18nTranslator(translations.button.checkResult)}
                      </Button>
                    </Badge>
                  )}
                </Stack>
              </Box>
              <FormikProvider value={formik}>
                <form noValidate onSubmit={formik.handleSubmit}>
                  <Box sx={{ mt: "20px", width: "100%" }}>
                    <Grid container xs={12} item columnSpacing={2}>
                      <Grid xs={3} item>
                        <WarehouseDropdown
                          currentWarehouse={currentWarehouse}
                          setCurrentWarehouse={setCurrentWarehouse}
                        />
                      </Grid>
                      <Grid xs={3} item>
                        <ZoneDropdown
                          currentWarehouse={currentWarehouse}
                          currentZone={currentZone}
                          setCurrentZone={setCurrentZone}
                        />
                      </Grid>
                      <Grid xs={3} item>
                        <FormTextField
                          disabled={
                            isEmpty(currentWarehouse) || isEmpty(currentZone)
                          }
                          id="product"
                          label={I18nTranslator(
                            translations.report.productName
                          )}
                          placeholder={I18nTranslator(
                            translations.placeholder.productName
                          )}
                        />
                      </Grid>
                      <Grid xs={3} item>
                        <FormTextField
                          disabled={
                            isEmpty(currentWarehouse) || isEmpty(currentZone)
                          }
                          id="productLot"
                          label={I18nTranslator(
                            translations.report.productLotOrPallet
                          )}
                          placeholder={I18nTranslator(
                            translations.placeholder.productLotOrPallet
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </form>
              </FormikProvider>
            </Box>
          </GridCardContent>
        </Box>

        {!isEmpty(currentZone) && (
          <WarehouseLayoutSvg
            warehouse={currentWarehouse}
            zoneCode={currentZone}
          />
        )}
      </BasePage>
      <Modal
        open={visible}
        onClose={() => setVisible(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "70%",
            bgcolor: "white",
            p: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              paddingBottom: "20px",
            }}
          >
            <Typography id="modal-modal-title" variant="h2" component="h2">
              Danh sách vị trí và pallet
            </Typography>
            <IconButton
              sx={{ backgroundColor: "grey" }}
              onClick={() => setVisible(false)}
            >
              <Close sx={{ color: "grey" }} fontSize={"small"} />
              <Typography
                sx={{ paddingLeft: "6px", color: "grey" }}
                fontWeight={"500"}
              >
                {I18nTranslator(translations.button.close)}
              </Typography>
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              overflow: "auto",
              height: "80vh",
            }}
          >
            <Box
              sx={{
                width: "200px",
                paddingRight: "20px",
              }}
            >
              <Typography variant="h3">
                {I18nTranslator(translations.report.searchResult)}
              </Typography>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent="space-between"
              >
                <Typography>
                  {I18nTranslator(translations.report.zone)}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {groupLocationListByZone(searchResult).length}
                </Typography>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent="space-between"
              >
                <Typography>
                  {I18nTranslator(translations.report.line)}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {sum(
                    groupLocationListByZone(searchResult).map(
                      (e) => e.values.length
                    )
                  )}
                </Typography>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent="space-between"
              >
                <Typography>
                  {I18nTranslator(translations.report.pallet)}
                </Typography>
                <Typography fontWeight={"bold"}>
                  {searchResult.length}
                </Typography>
              </Box>
            </Box>
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {groupLocationListByZone(searchResult).map((zone, index) => {
                return (
                  <Box
                    sx={{
                      padding: "10px",
                      marginBottom: "10px",
                      border: "1px solid black",
                    }}
                  >
                    <Typography variant="h2">
                      {isEmpty(zone.zoneName)
                        ? I18nTranslator(translations.report.noZone)
                        : zone.zoneName}
                    </Typography>
                    <Box
                      sx={{
                        padding: "10px 0 10px 0",
                        borderRadius: "10px",
                      }}
                    >
                      {zone.values.map((line, lineIndex) => {
                        return (
                          <Box sx={{ paddingBottom: "10px" }}>
                            <Typography variant="h3">
                              {(isEmpty(line.lineName)
                                ? ""
                                : line.lineName + "-") +
                                `(${line.values.length} pallet)`}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                padding: "10px",
                                backgroundColor: "#f5f5f5",
                                borderRadius: "5px",
                              }}
                            >
                              {line.values.map((pallet, palletIndex) => (
                                <Box
                                  sx={{
                                    padding: "12px",
                                    margin: "4px 4px 4px 0",
                                    backgroundColor: "#fff79e",
                                    borderRadius: "5px",
                                  }}
                                >
                                  <Typography>{pallet.locationName}</Typography>
                                  <Typography
                                    fontWeight={"bold"}
                                    color={"black"}
                                  >
                                    {pallet.palletCode}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Modal>
    </WarehouseLayoutContext.Provider>
  );
};

const WarehouseLayoutSvg = (props: { warehouse: string; zoneCode: string }) => {
  const { warehouse, zoneCode } = props;
  const { getWarehouseLoading, getZoneLoading, searchResult } =
    useWarehouseLayoutContext();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [lineFilter, setLineFilter] = useState<
    {
      zoneCode: string;
      lineCode: string;
    }[]
  >([]);

  const requestZoneLine = useMemo(
    () => ({
      zoneCode: props.zoneCode === "a" ? "" : props.zoneCode,
      pageNumber: FIRST_PAGE,
      pageSize: 100,
      orderBy: "LineCode",
      warehouseId: props.warehouse,
    }),
    [zoneCode, warehouse]
  );

  const { loading, result: resultZoneline } = usePaginationRequest<
    IGetZoneLineRequest,
    IZoneLine[]
  >(requestZoneLine, ZoneApiService.getAllZoneLines, [requestZoneLine], {
    holdRequest: isEmpty(warehouse) || isEmpty(zoneCode),
  });

  const getZoneLineSummary = () => {
    return resultZoneline.data?.filter((zoneline) =>
      lineFilter.some(
        (line) =>
          line.lineCode === zoneline.lineCode &&
          line.zoneCode === zoneline.zoneCode
      )
    );
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <GridCardContent sx={{ flexDirection: "column", p: 2 }}>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={4} item>
          <Box sx={{ paddingBottom: "40px" }}>
            <Typography variant="h3">
              {I18nTranslator(translations.report.zoneLineList)}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                paddingTop: "20px",
              }}
            >
              {loading ? (
                <CircularProgress />
              ) : (
                <>
                  {resultZoneline.data?.map((e, i) => {
                    return (
                      <Badge
                        color="primary"
                        badgeContent={
                          searchResult?.filter(
                            (result) => result.lineCode === e.lineCode
                          ).length || 0
                        }
                        style={{
                          width: e.lineName.length > 10 ? "48%" : "23%",
                          margin: "1%",
                        }}
                      >
                        <Button
                          style={{
                            paddingTop: "10px",
                            paddingBottom: "10px",
                            borderRadius: 4,
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          fullWidth
                          className={"locationPallet"}
                          variant={
                            lineFilter.some(
                              (line) =>
                                line.lineCode === e.lineCode &&
                                line.zoneCode === e.zoneCode
                            )
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() => {
                            if (
                              lineFilter.some(
                                (line) =>
                                  line.lineCode === e.lineCode &&
                                  line.zoneCode === e.zoneCode
                              )
                            ) {
                              setLineFilter(
                                filter(
                                  lineFilter,
                                  (line) =>
                                    line.lineCode !== e.lineCode ||
                                    line.zoneCode !== e.zoneCode
                                )
                              );
                            } else {
                              setLineFilter(
                                orderBy(
                                  [
                                    ...lineFilter,
                                    {
                                      lineCode: e.lineCode,
                                      zoneCode: e.zoneCode,
                                    },
                                  ],
                                  ["lineCode"]
                                )
                              );
                            }
                          }}
                        >
                          {e.lineName}
                        </Button>
                      </Badge>
                    );
                  })}
                </>
              )}
            </Box>
          </Box>

          {(!isEmpty(lineFilter) || lineFilter.length !== 0) && (
            <Box>
              <Typography variant="h3">
                {I18nTranslator(translations.report.summary)}
              </Typography>
              <Box sx={{ backgroundColor: "#f5f5f5" }}>
                <>
                  {getZoneLineSummary()?.map((e) => (
                    <Box
                      sx={{
                        padding: "10px",
                        marginTop: "10px",
                      }}
                    >
                      <Typography variant="h4">
                        {I18nTranslator(translations.report.line)} {e.lineName}
                      </Typography>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent="space-between"
                      >
                        <Typography>
                          {I18nTranslator(translations.report.location)}
                        </Typography>
                        <Typography fontWeight={"bold"}>
                          {e.totalLocation}
                        </Typography>
                      </Box>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent="space-between"
                      >
                        <Typography>
                          {I18nTranslator(
                            translations.report.locationHasProduct
                          )}
                        </Typography>
                        <Typography fontWeight={"bold"}>
                          {e.totalLocationNoEmpty}
                        </Typography>
                      </Box>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent="space-between"
                      >
                        <Typography>
                          {I18nTranslator(translations.report.emptyLocation)}
                        </Typography>
                        <Typography fontWeight={"bold"}>
                          {e.totalLocationEmpty}
                        </Typography>
                      </Box>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent="space-between"
                      >
                        <Typography>
                          {I18nTranslator(translations.report.totalPallet)}
                        </Typography>
                        <Typography fontWeight={"bold"}>
                          {e.totalPalletQuantity}
                        </Typography>
                      </Box>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent="space-between"
                      >
                        <Typography>
                          {I18nTranslator(translations.report.product)}
                        </Typography>
                        <Typography fontWeight={"bold"}>
                          {e.totalProductQuantity}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </>
                <Box
                  sx={{
                    padding: "10px",
                    marginTop: "10px",
                    borderTop: 1,
                    borderColor: "gray",
                  }}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent="space-between"
                  >
                    <Typography>
                      {I18nTranslator(translations.report.totalLocation)}
                    </Typography>
                    <Typography fontWeight={"bold"}>
                      {sum(
                        getZoneLineSummary()?.map((e) => {
                          return toNumber(e.totalLocation);
                        })
                      )}
                    </Typography>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent="space-between"
                  >
                    <Typography>
                      {I18nTranslator(
                        translations.report.totalLocationHasProduct
                      )}
                    </Typography>
                    <Typography fontWeight={"bold"}>
                      {sum(
                        getZoneLineSummary()?.map((e) => {
                          return toNumber(e.totalLocationNoEmpty);
                        })
                      )}
                    </Typography>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent="space-between"
                  >
                    <Typography>
                      {I18nTranslator(translations.report.totalEmptyLocation)}
                    </Typography>
                    <Typography fontWeight={"bold"}>
                      {sum(
                        getZoneLineSummary()?.map((e) => {
                          return toNumber(e.totalLocationEmpty);
                        })
                      )}
                    </Typography>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent="space-between"
                  >
                    <Typography>
                      {I18nTranslator(translations.report.totalPallet)}
                    </Typography>
                    <Typography fontWeight={"bold"}>
                      {sum(
                        getZoneLineSummary()?.map((e) => {
                          return toNumber(e.totalPalletQuantity);
                        })
                      )}
                    </Typography>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent="space-between"
                  >
                    <Typography>
                      {I18nTranslator(translations.report.totalProduct)}
                    </Typography>
                    <Typography fontWeight={"bold"}>
                      {sum(
                        getZoneLineSummary()?.map((e) => {
                          return toNumber(e.totalProductQuantity || 0);
                        })
                      ).toFixed(3)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Grid>
        <Grid xs={8} item>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "10px",
            }}
          >
            <Button
              aria-describedby={id}
              startIcon={<SpeakerNotes />}
              variant="contained"
              onClick={handleClick}
            >
              {I18nTranslator(translations.report.note)}
            </Button>
            <LegendModal
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
            />
          </Box>
          {resultZoneline.data
            ?.filter((zoneline) =>
              lineFilter.some(
                (line) =>
                  line.lineCode === zoneline.lineCode &&
                  line.zoneCode === zoneline.zoneCode
              )
            )
            .map((line) => {
              return <ZoneLineSection zoneLine={line} />;
            })}
        </Grid>
      </Grid>
    </GridCardContent>
  );
};

const ZoneLineSection = (props: { zoneLine: IZoneLine }) => {
  const { zoneLine } = props;

  const [groupLocation, setGroupLocation] = useState<any[]>([]);
  const request = useMemo(
    () => ({
      zoneCode: zoneLine.zoneCode,
      pageNumber: FIRST_PAGE,
      pageSize: 999999,
      orderBy: "locationName",
      warehouseId: zoneLine.warehouseId,
      searchText: zoneLine.lineCode,
    }),
    [zoneLine]
  );
  const { loading, result } = usePaginationRequest<
    IGetAllLocationByZoneRequest,
    ILocation[]
  >(request, MasterDataApiService.getAllLocationsByProductLot, [request]);

  const groupPalletByLocationCode = (data: ILocation[]) => {
    return chain(data)
      .groupBy("locationCode")
      .map((value, key) => ({
        locationCode: value[0].locationCode,
        level: value[0].levelID,
        length: value[0].lengthID,
        value,
      }))
      .value();
  };

  const isFloor = (line: IZoneLine) => {
    return (
      line.lineCode.startsWith("S") &&
      (line.zoneCode.startsWith("S") || zoneLine.zoneCode.startsWith("D"))
    );
  };

  useEffect(() => {
    if (result.data) {
      setGroupLocation(groupPalletByLocationCode(result.data));
    }
  }, [result]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "end",
        position: "relative",
        marginBottom: "40px",
      }}
    >
      <Box
        paddingLeft={"10px"}
        paddingRight={"10px"}
        paddingTop={"10px"}
        paddingBottom={"10px"}
      >
        <Typography variant="h3" fontWeight={"bold"}>
          {I18nTranslator(translations.report.line)} {zoneLine.lineName}
        </Typography>
      </Box>
      {loading && (
        <Box
          sx={{
            display: "flex",
            position: "absolute",
            width: isFloor(zoneLine)
              ? 500 + 20 + "px"
              : zoneLine.length * 50 + 40 + "px",
            height: zoneLine.level * 50 + (isFloor(zoneLine) ? 20 : 40) + "px",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f5f5f580",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        width={
          isFloor(zoneLine) ? 500 + 20 + "px" : zoneLine.length * 50 + 40 + "px"
        }
        height={zoneLine.level * 50 + (isFloor(zoneLine) ? 20 : 40) + "px"}
        style={{
          display: "flex",
          pointerEvents: "all",
          backgroundColor: "#f5f5f5",
        }}
      >
        <defs>
          <svg id="pallet" viewBox="0 0 24 24" height={40} width={40}>
            {/* <img src={require("../../assets/images/pallet.svg")} /> */}
            <path d="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L10.11,5.22L16,8.61L17.96,7.5L12,4.15M6.04,7.5L12,10.85L13.96,9.75L8.08,6.35L6.04,7.5M5,15.91L11,19.29V12.58L5,9.21V15.91M19,15.91V9.21L13,12.58V19.29L19,15.91Z" />
          </svg>
          <rect id="r" width="50" height="50" x="0" />
          <rect id="floor" width="500" height="50" x="0" />
        </defs>
        <g transform={`translate(${isFloor(zoneLine) ? 10 : 30},10)`}>
          {isFloor(zoneLine) ? (
            <FloorLocationItem
              currentLocation={groupLocation?.find(
                (location) => location.level === 0 && location.length === 1
              )}
            />
          ) : (
            <>
              {Array(zoneLine.length)
                .fill(0)
                .map((e, i) => {
                  return Array(zoneLine.level)
                    .fill(0)
                    .map((e2, i2) => {
                      const currentLenght = i + 1;
                      const currentLevel = i2 + 1;
                      return (
                        <LocationItem
                          columnIndex={i}
                          rowIndex={i2}
                          currentLocation={groupLocation?.find(
                            (location) =>
                              location.level === zoneLine.level - i2 &&
                              location.length === currentLenght
                          )}
                        />
                      );
                    });
                })}
            </>
          )}
        </g>

        {!isFloor(zoneLine) && (
          <g transform="translate(10,-10)">
            {Array(zoneLine.level)
              .fill(0)
              .map((e, i) => (
                <text y={zoneLine.level * 50 - 50 * i}>{i + 1}</text>
              ))}
          </g>
        )}

        {!isFloor(zoneLine) && (
          <g
            transform={`translate(${zoneLine.length + 40},${
              zoneLine.level * 50 + 30
            })`}
          >
            {Array(zoneLine.length)
              .fill(0)
              .map((e, i) => (
                <text x={i * 50}>{i + 1}</text>
              ))}
          </g>
        )}
      </svg>
    </Box>
  );
};

const LocationItem = (props: {
  rowIndex: number;
  columnIndex: number;
  currentLocation?: {
    locationCode: string;
    level: string;
    length: string;
    value: ILocation[];
  };
}) => {
  const { searchResult } = useWarehouseLayoutContext();
  const { columnIndex, rowIndex, currentLocation } = props;
  const [isHover, setHover] = useState(false);

  const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }: any) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      width: 400,
    },
  }));
  return (
    <LightTooltip
      followCursor
      onOpen={() => {
        if (!isHover) {
          setHover(true);
        }
      }}
      onClose={() => setHover(false)}
      title={
        <Box component="span" p={1} width={500}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent="space-between"
          >
            <Typography>
              {I18nTranslator(translations.report.location)}
            </Typography>
            <Typography fontWeight={"bold"}>
              {currentLocation?.value[0].locationName}
            </Typography>
          </Box>
          {currentLocation?.value[0].currentPalletCode && (
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent="space-between"
              sx={{ marginBottom: "30px" }}
            >
              <Typography>
                {I18nTranslator(translations.report.palletCode)}
              </Typography>
              <Typography fontWeight={"bold"}>
                {currentLocation?.value[0].currentPalletCode}
              </Typography>
            </Box>
          )}
          {currentLocation?.value[0].currentPalletCode && (
            <>
              {currentLocation?.value.map((productLot, i) => {
                return (
                  <>
                    {i !== 0 && (
                      <div
                        style={{
                          height: 1,
                          backgroundColor: "white",
                        }}
                      />
                    )}
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent="space-between"
                      sx={{ marginTop: "10px" }}
                    >
                      <Typography>
                        {I18nTranslator(translations.report.productLot)}
                      </Typography>
                      <Typography fontWeight={"bold"}>
                        {productLot?.productLot}
                      </Typography>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent="space-between"
                    >
                      <Typography>
                        {I18nTranslator(translations.report.productCode)}
                      </Typography>
                      <Typography fontWeight={"bold"}>
                        {productLot?.productCode}
                      </Typography>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent="space-between"
                    >
                      <Typography>
                        {I18nTranslator(translations.report.productCodeOld)}
                      </Typography>
                      <Typography fontWeight={"bold"}>
                        {productLot?.productCodeOld}
                      </Typography>
                    </Box>
                    <Box
                      flex={1}
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent="space-between"
                    >
                      <Typography flex={1}>
                        {I18nTranslator(translations.report.productName)}
                      </Typography>
                      <Typography
                        textAlign={"end"}
                        flex={1}
                        fontWeight={"bold"}
                      >
                        {productLot?.productName}
                      </Typography>
                    </Box>
                    <Box
                      flex={1}
                      display={"flex"}
                      flexDirection={"row"}
                      justifyContent="space-between"
                      sx={{ marginBottom: "10px" }}
                    >
                      <Typography>
                        {I18nTranslator(translations.report.productQuantity)}
                      </Typography>
                      <Typography fontWeight={"bold"}>
                        {productLot?.palletQuantity}
                      </Typography>
                    </Box>
                  </>
                );
              })}
            </>
          )}
        </Box>
      }
    >
      <g transform={`translate(${columnIndex * 50},  ${rowIndex * 50})`}>
        <use
          xlinkHref="#r"
          style={{
            stroke: "black",
            fill:
              currentLocation?.value[0].currentPalletCode === null
                ? "#dedede"
                : searchResult?.findIndex(
                    (e) => e.locationCode === currentLocation?.locationCode
                  ) !== -1
                ? "#fa5c5c"
                : "white",
            strokeWidth: "1px",
          }}
        />

        {currentLocation?.value[0].currentPalletCode !== null && (
          <use
            xlinkHref="#pallet"
            x={5}
            y={5}
            style={{
              fill:
                searchResult?.findIndex(
                  (e) => e.locationCode === currentLocation?.locationCode
                ) !== -1
                  ? "white"
                  : "#0072CE",
            }}
          />
        )}
      </g>
    </LightTooltip>
  );
};

const FloorLocationItem = (props: {
  currentLocation?: {
    locationCode: string;
    level: string;
    length: string;
    value: ILocation[];
  };
}) => {
  const { currentLocation } = props;
  const { searchResult } = useWarehouseLayoutContext();

  const [isHover, setHover] = useState(false);

  const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }: any) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      width: 400,
    },
  }));

  const getPalletQuantityOfFloor = () => {
    return (
      searchResult?.filter(
        (result) => result.locationCode === currentLocation?.locationCode
      ) || []
    );
  };

  return (
    <LightTooltip
      followCursor
      onOpen={() => {
        if (!isHover) {
          setHover(true);
        }
      }}
      onClose={() => setHover(false)}
      title={
        <Box component="span" p={1} width={500}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent="space-between"
          >
            <Typography>
              {I18nTranslator(translations.report.location)}
            </Typography>
            <Typography fontWeight={"bold"}>
              {currentLocation?.value[0].locationCode}
            </Typography>
          </Box>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent="space-between"
          >
            <Typography>
              {I18nTranslator(translations.report.totalPallet)}
            </Typography>
            <Typography fontWeight={"bold"}>
              {currentLocation?.value.length}
            </Typography>
          </Box>
        </Box>
      }
    >
      <g transform={`translate(0,0)`}>
        <use xlinkHref="#floor" x={0} fill={"white"} />
        {currentLocation?.value[0].currentPalletCode !== null && (
          <>
            {take(currentLocation?.value, 10).map((location, i) => {
              return (
                <>
                  <use
                    xlinkHref="#r"
                    x={i * 50}
                    style={{
                      fill: !isEmpty(getPalletQuantityOfFloor()[i])
                        ? "#fa5c5c"
                        : "white",
                    }}
                  />
                  <use
                    xlinkHref="#pallet"
                    x={i * 50 + 5}
                    y={5}
                    style={{
                      fill: !isEmpty(getPalletQuantityOfFloor()[i])
                        ? "white"
                        : "#0072CE",
                    }}
                  />
                </>
              );
            })}
          </>
        )}
      </g>
    </LightTooltip>
  );
};

const WarehouseDropdown = (props: {
  currentWarehouse: string;
  setCurrentWarehouse: (warehouseId: string) => void;
}) => {
  const { setGetWarehouseLoading } = useWarehouseLayoutContext();
  const request = useMemo(
    () => ({
      pageNumber: FIRST_PAGE,
      pageSize: 1000,
    }),
    []
  );
  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    IWarehouse[]
  >(request, MasterDataApiService.getAllWarehouses, []);

  useEffect(() => {
    setGetWarehouseLoading && setGetWarehouseLoading(loading);
  }, [loading]);

  const handleChange = (event: SelectChangeEvent) => {
    props.setCurrentWarehouse(event.target.value as string);
  };
  return (
    <FormControl fullWidth>
      <InputLabel id="demo-simple-select-label">
        {I18nTranslator(translations.masterData.warehouse)}
      </InputLabel>
      <Select
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={props.currentWarehouse}
        label={I18nTranslator(translations.masterData.warehouse)}
        onChange={handleChange}
      >
        {result.data?.map((e) => {
          return <MenuItem value={e.id}>{e.description}</MenuItem>;
        })}
      </Select>
    </FormControl>
  );
};

const ZoneDropdown = (props: {
  currentWarehouse: string;
  currentZone: string;
  setCurrentZone: (zoneId: string) => void;
}) => {
  const { setGetZoneLoading } = useWarehouseLayoutContext();

  const request = useMemo(
    () => ({
      pageNumber: FIRST_PAGE,
      pageSize: 1000,
      warehouseId: props.currentWarehouse,
      orderBy: "zoneCode",
    }),
    [props.currentWarehouse]
  );
  const { loading, result } = usePaginationRequest<IPaginationRequest, IZone[]>(
    request,
    MasterDataApiService.getAllZones,
    [request]
  );

  useEffect(() => {
    setGetZoneLoading && setGetZoneLoading(loading);
  }, []);

  const handleChange = (event: SelectChangeEvent) => {
    props.setCurrentZone(event.target.value as string);
  };

  return (
    <FormControl fullWidth>
      <InputLabel id="demo-simple-select-label">
        {I18nTranslator(translations.masterData.zone)}
      </InputLabel>
      <Select
        disabled={isEmpty(props.currentWarehouse)}
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={props.currentZone}
        label={I18nTranslator(translations.masterData.zone)}
        onChange={handleChange}
      >
        {result.data?.map((e) => {
          return <MenuItem value={e.zoneCode}>{e.description}</MenuItem>;
        })}
      </Select>
    </FormControl>
  );
};

export default WarehouseLayout;
